/* global BigInt */
import React, { useState, useRef, useEffect } from 'react';

/**
 * MotionGridEditor — the 8×8 motion-detection grid, as an INLINE field with a deliberate edit mode
 * (unified-viewer-setup-design.md §4.2; David 2026-07-21).
 *
 * Ported from dragon's on-device editor (`dragon/html/config.html` openGridEditor). The mask is a
 * 64-bit value stored as a 16-hex-digit string ("0x" + 16 hex). Bit ordering (dragon-exact):
 *   bit = row * 8 + col   (row 0 = top, col 0 = left)   →   bit 0 = top-left, bit 63 = bottom-right
 * A SET bit = motion detection ENABLED for that cell (shown clear over the frame); a CLEARED bit =
 * disabled (shown with a dark scrim). Shared by dragon AND legacy families (both store
 * `motion_grid_mask`).
 *
 * Rendered inline in the Camera → Video tab (NOT a separate page): a locked read-only preview by
 * default (so stray taps can't change it), an "Edit" button unlocks it for painting + shows the
 * Select All / Clear / Invert tools, "Done" re-locks. Edits are LIVE (they patch the camera form
 * via onChange); the camera's own Save / Revert are the commit / undo — no per-grid Apply/Cancel.
 *
 * The pure round-trip (maskToCells / cellsToMask) is exported for unit testing. 64-bit math uses
 * BigInt via the function form (no `1n` literals) to stay safe across the CRA/Babel/Jest toolchain.
 */
export const GRID = 8;
export const CELLS = GRID * GRID;
const B0 = BigInt(0);
const B1 = BigInt(1);
const ALL = (B1 << BigInt(CELLS)) - B1; // 0xffffffffffffffff

/** Parse a mask string (e.g. "0xffffffffffffffff") to a BigInt; defaults to all-enabled. */
const parseMask = (val) => {
  const s = String(val == null ? '' : val).trim();
  if (!s) return ALL;
  try {
    const b = BigInt(s.startsWith('0x') || s.startsWith('0X') ? s : `0x${s}`);
    return b & ALL;
  } catch (e) {
    return ALL;
  }
};

/** BigInt mask → canonical "0x" + 16-hex-digit string (lowercase, matches dragon's toString(16)). */
const formatMask = (mask) => `0x${(mask & ALL).toString(16).padStart(16, '0')}`;

/**
 * A mask string → a 64-length boolean array (index = row*8+col; true = motion enabled).
 * @param {string} val
 * @returns {boolean[]}
 */
export const maskToCells = (val) => {
  const mask = parseMask(val);
  const cells = new Array(CELLS);
  for (let bit = 0; bit < CELLS; bit++) cells[bit] = ((mask >> BigInt(bit)) & B1) === B1;
  return cells;
};

/**
 * A 64-length boolean array → a canonical mask string ("0x" + 16 hex). Inverse of maskToCells.
 * @param {boolean[]} cells
 * @returns {string}
 */
export const cellsToMask = (cells) => {
  let mask = B0;
  for (let bit = 0; bit < CELLS; bit++) {
    if (cells && cells[bit]) mask |= (B1 << BigInt(bit));
  }
  return formatMask(mask);
};

const MotionGridEditor = ({ label, hint, value, snapshotUrl, disabled, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const dragRef = useRef({ active: false, set: null });
  const cells = maskToCells(value); // controlled: cells derive from the current value

  // A frame is required to edit the mask — painting blind produces false masking (David 2026-07-23).
  // hasImage tracks "a snapshot URL that actually loaded"; reset on camera switch (new snapshotUrl).
  const hasImage = !!snapshotUrl && imgOk;
  useEffect(() => { setImgOk(true); }, [snapshotUrl]);
  useEffect(() => { if ((!hasImage || disabled) && editing) setEditing(false); }, [hasImage, disabled, editing]);

  const setCell = (i, on) => {
    if (cells[i] === on) return;
    const next = cells.slice();
    next[i] = on;
    if (onChange) onChange(cellsToMask(next));
  };

  // Paint (mouse + touch) — only while editing. The first cell decides the paint value; drag paints.
  const onCellDown = (i) => (e) => {
    if (!editing) return;
    e.preventDefault();
    const set = !cells[i];
    dragRef.current = { active: true, set };
    setCell(i, set);
  };
  const onCellEnter = (i) => () => {
    if (editing && dragRef.current.active) setCell(i, dragRef.current.set);
  };
  const onGridTouchMove = (e) => {
    if (!editing || !dragRef.current.active) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    const el = document.elementFromPoint(t.clientX, t.clientY);
    const idx = el && el.getAttribute && el.getAttribute('data-cell');
    if (idx != null && idx !== '') setCell(Number(idx), dragRef.current.set);
  };
  const endDrag = () => { dragRef.current = { active: false, set: null }; };

  const selectAll = () => onChange && onChange(cellsToMask(new Array(CELLS).fill(true)));
  const clearAll = () => onChange && onChange(cellsToMask(new Array(CELLS).fill(false)));
  const invert = () => onChange && onChange(cellsToMask(cells.map((c) => !c)));

  return (
    <div className={'setup-maskfield' + (disabled ? ' setup-maskfield--disabled' : '')}>
      <div className="setup-maskfield__head">
        <div className="setup-maskfield__headleft">
          <div className="setup-maskfield__label">{label}</div>
          {hint ? <div className="setup-maskfield__desc">{hint}</div> : null}
        </div>
        <div className="setup-maskfield__headbtns">
          {editing ? (
            <>
              <button className="setup-maskfield__toolbtn" onClick={selectAll}>All</button>
              <button className="setup-maskfield__toolbtn" onClick={clearAll}>Clear</button>
              <button className="setup-maskfield__toolbtn" onClick={invert}>Invert</button>
            </>
          ) : null}
          <button
            className="setup-maskfield__toolbtn"
            disabled={!hasImage || disabled}
            title={disabled ? 'Read-only — you do not have edit access.' : (!hasImage ? 'No snapshot available — editing unavailable.' : undefined)}
            onClick={() => setEditing((v) => !v)}
          >{editing ? 'Done' : 'Edit'}</button>
        </div>
      </div>

      <div
        className={'setup-maskfield__frame' + (editing ? ' setup-maskfield__frame--editing' : '')}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onTouchEnd={endDrag}
        onTouchCancel={endDrag}
      >
        {snapshotUrl && imgOk ? (
          <img className="setup-maskfield__img" src={snapshotUrl} alt="camera snapshot" onError={() => setImgOk(false)} draggable={false} />
        ) : (
          <div className="setup-maskfield__noimg">No snapshot</div>
        )}
        <div className="setup-mask__grid" onTouchMove={onGridTouchMove}>
          {cells.map((on, i) => (
            <div
              key={i}
              data-cell={i}
              className={'setup-mask__cell ' + (on ? 'setup-mask__cell--on' : 'setup-mask__cell--off')}
              onMouseDown={onCellDown(i)}
              onMouseEnter={onCellEnter(i)}
              onTouchStart={onCellDown(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MotionGridEditor;
